import "../scss/_navmenu.scss";

import { useContext, useRef, useState } from "react";

import { THEMES, THEMES_LIST } from "../constants/themes";
import { SCALES, SCALES_LIST } from "../constants/fontScales";

import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { setFontScale, setTheme } from "../features/settings";
import useKlasisSearchNavigator from "../utils/klasisSearchNavigator";
import _ from "lodash";

import SearchIcon from "@mui/icons-material/Search";
import LanguageIcon from "@mui/icons-material/Language";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuIcon from "@mui/icons-material/Menu";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

import {
    Link as RouterLink,
    useLocation,
    useNavigate,
    useParams,
} from "react-router-dom";
import { useTheme, ThemeProvider } from "@mui/material/styles";
import {
    Stack,
    Toolbar,
    Button,
    styled,
    Divider,
    IconButton,
    useMediaQuery,
    Popover,
    Box,
    Collapse,
    Typography,
    Grid,
    Drawer,
    Link,
} from "@mui/material";
import SearchEdit from "../components/inputs/SearchEdit";

import logo from "../assets/images/logo.svg";
import logoInverted from "../assets/images/logo-inverted.svg";

import { openModal } from "../features/modals";
import MODALS from "../constants/modals";
import useKlasisRouting from "../utils/klasisRouting";
import { Translations } from "../localization/translations";
import { LocaleContext } from "../providers/LocaleProvider";

const NavbarButton = styled(Button)(({ theme }) => ({
    textTransform: "none",
    flexDirection: "column",
    color: "inherit",
    "& .MuiButton-startIcon": {
        margin: 0,
    },
}));

const NavMenu: React.FC = () => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up("sm"));
    const { klasisRoute } = useKlasisRouting();

    const settings = useAppSelector((state) => state.settings);

    return (
        <Toolbar
            component={Stack}
            direction="row"
            className="klasis-toolbar"
            spacing={1}
            justifyContent="space-between"
            sx={(theme) => ({
                backgroundColor: theme.palette.background.default,
            })}
        >
            <RouterLink to={klasisRoute("")}>
                <img
                    src={
                        [THEMES.default, THEMES.blackOnYellow].includes(
                            settings.theme
                        )
                            ? logo
                            : logoInverted
                    }
                    alt="KLASIS"
                />
            </RouterLink>
            {matches ? (
                <>
                    <Divider orientation="vertical" flexItem />
                    <SearchNavbarItem />
                    <Divider orientation="vertical" flexItem />
                    <LanguageNavbarItem />
                    <Divider orientation="vertical" flexItem />
                    <SettingsNavbarItem />
                </>
            ) : (
                <HamburgerNavbarItem />
            )}
        </Toolbar>
    );
};

const SearchNavbarItem: React.FC = () => {
    const searchInputRef = useRef<HTMLElement>();
    const [searchPhrase, setSearchPhrase] = useState<string>("");

    const { t } = useTranslation();
    const { klasisSearchNavigate } = useKlasisSearchNavigator();

    const [open, setOpen] = useState(false);

    const handleSearch = (searchPhrase: string): void => {
        klasisSearchNavigate(searchPhrase);
        setSearchPhrase("");
    };

    const toggleSearchInput = () => {
        setOpen(!open);
    };

    return (
        <>
            <Box
                style={{
                    display: "flex",
                    flexGrow: 1,
                    justifyContent: "flex-end",
                }}
            >
                <Collapse
                    orientation="horizontal"
                    in={open}
                    addEndListener={() => {
                        if (open) {
                            searchInputRef?.current?.focus();
                        }
                    }}
                >
                    <SearchEdit
                        ref={searchInputRef}
                        value={searchPhrase}
                        label={t(Translations.navBar.label.searchClassifier)}
                        onSearch={handleSearch}
                        onSeachClear={() => {
                            setSearchPhrase("");
                        }}
                        onChange={(event) => {
                            setSearchPhrase(event.target.value);
                        }}
                    />
                </Collapse>
            </Box>
            <NavbarButton
                startIcon={<SearchIcon />}
                onClick={toggleSearchInput}
            >
                {t(Translations.navBar.button.search)}
            </NavbarButton>
        </>
    );
};

export const LanguageNavbarItem: React.FC = () => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "language-menu" : undefined;

    return (
        <>
            <NavbarButton
                aria-describedby={id}
                startIcon={<LanguageIcon />}
                onClick={handleOpen}
            >
                {t(Translations.navBar.button.language)}
            </NavbarButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Languages />
            </Popover>
        </>
    );
};

interface ISettingsNavbarItemProps {
    minimal?: boolean;
}

export const SettingsNavbarItem: React.FC<ISettingsNavbarItemProps> = ({
    minimal,
}) => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "settings-popover" : undefined;

    return (
        <>
            <NavbarButton
                aria-describedby={id}
                startIcon={<SettingsIcon />}
                onClick={handleOpen}
            >
                {t(Translations.navBar.button.settings)}
            </NavbarButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Settings
                    hideSearch={minimal}
                    hideAccessibility={minimal}
                    hideCookieSettings={minimal}
                    closeCallback={handleClose}
                />
            </Popover>
        </>
    );
};

const HamburgerNavbarItem: React.FC = () => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <IconButton
                sx={{ color: "text.primary" }}
                onClick={() => setOpen(true)}
            >
                <MenuIcon />
            </IconButton>
            <Drawer anchor="right" open={open} onClick={() => setOpen(false)}>
                <Grid container justifyContent="flex-end" sx={{ padding: 2 }}>
                    <Grid item>
                        <IconButton sx={{ color: "text.primary" }}>
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
                <Settings />
            </Drawer>
        </>
    );
};

interface ISettingsProps {
    hideSearch?: boolean;
    hideAccessibility?: boolean;
    hideCookieSettings?: boolean;
    closeCallback?: () => void;
}

const Settings: React.FC<ISettingsProps> = ({
    hideSearch,
    hideAccessibility,
    hideCookieSettings,
    closeCallback,
}) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up("sm"));
    const { t } = useTranslation();
    const { klasisRoute } = useKlasisRouting();
    const { klasisSearchNavigate } = useKlasisSearchNavigator();
    const dispatch = useAppDispatch();
    const [searchPhrase, setSearchPhrase] = useState<string>("");

    const handleSearch = (searchPhrase: string): void => {
        klasisSearchNavigate(searchPhrase);
    };

    return (
        <Stack spacing={2} minWidth={350} sx={{ paddingBottom: 2 }}>
            {!matches && !hideSearch ? (
                <>
                    <SettingsItem>
                        <SearchEdit
                            value={searchPhrase}
                            label={t(
                                Translations.navBar.label.searchClassifier
                            )}
                            onSearch={handleSearch}
                            onChange={(event) => {
                                setSearchPhrase(event.target.value);
                            }}
                        />
                    </SettingsItem>
                    <SettingsItem label={t(Translations.navBar.label.language)}>
                        <Languages />
                    </SettingsItem>
                    <Divider flexItem />
                </>
            ) : null}
            {!hideAccessibility ? (
                <SettingsItem
                    label={t(Translations.navBar.label.accessibility)}
                >
                    <Link
                        component={RouterLink}
                        to={klasisRoute("easy-to-read")}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => {
                                if (closeCallback) {
                                    closeCallback();
                                }
                            }}
                        >
                            {t(Translations.common.button.open)}
                        </Button>
                    </Link>
                </SettingsItem>
            ) : null}
            <Divider flexItem />
            <SettingsItem label={t(Translations.navBar.label.fontSize)}>
                <Scales />
            </SettingsItem>
            <Divider flexItem />
            <SettingsItem label={t(Translations.navBar.label.contrast)}>
                <Themes />
            </SettingsItem>
            {!hideCookieSettings ? (
                <>
                    <Divider flexItem />
                    <SettingsItem
                        label={t(Translations.navBar.label.cookieSettings)}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => {
                                if (closeCallback) {
                                    closeCallback();
                                }
                                dispatch(
                                    openModal({
                                        type: MODALS.CookieConsent,
                                        ignoreIfOpen: true,
                                    })
                                );
                            }}
                        >
                            {t(Translations.common.button.open)}
                        </Button>
                    </SettingsItem>
                </>
            ) : null}
        </Stack>
    );
};

const LanguageButton = styled(Button)(({ theme }) => ({
    textTransform: "none",
    borderRadius: 0,
    color: theme.palette.text.primary,
    "&.active": {
        color: theme.palette.primary.main,
    },
}));

const Languages: React.FC = () => {
    const { locale } = useParams();
    let location = useLocation();
    const navigate = useNavigate();

    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up("sm"));

    const localeContext = useContext(LocaleContext);

    return (
        <Stack direction={matches ? "column" : "row"}>
            {localeContext?.locales
                .map((item) => (
                    <LanguageButton
                        fullWidth
                        className={locale === item.code ? "active" : undefined}
                        key={item.code}
                        variant="text"
                        onClick={() => {
                            const regex = new RegExp(`^/${locale}`);

                            navigate(
                                location.pathname.replace(
                                    regex,
                                    `/${item.code}`
                                ) +
                                location.search
                            );
                        }}
                    >
                        {item.name}
                    </LanguageButton>
                ))
                .reduce((previousValue, currentValue) => (
                    <>
                        {previousValue}
                        <Divider
                            orientation={matches ? "horizontal" : "vertical"}
                            flexItem
                        />
                        {currentValue}
                    </>
                ))}
        </Stack>
    );
};

const Scales: React.FC = () => {
    const dispatch = useAppDispatch();

    const selectedFontScale = useAppSelector(
        (state) => state.settings.fontScale
    );

    return (
        <Grid container spacing={1}>
            {_.map(SCALES_LIST, (scale, key) => (
                <Grid key={key} item xs>
                    <Button
                        variant={
                            selectedFontScale === key ? "contained" : "outlined"
                        }
                        fullWidth
                        onClick={() => dispatch(setFontScale(key as SCALES))}
                    >
                        {scale.size}%
                    </Button>
                </Grid>
            ))}
        </Grid>
    );
};

const Themes: React.FC = () => {
    const dispatch = useAppDispatch();

    const selectedTheme = useAppSelector((state) => state.settings.theme);

    return (
        <Grid container spacing={1}>
            {_.map(THEMES_LIST, (theme, key) => (
                <Grid key={key} item xs={5}>
                    <ThemeProvider theme={theme.theme}>
                        <Button
                            disabled={selectedTheme === key}
                            variant={"contained"}
                            fullWidth
                            color="secondary"
                            onClick={() => dispatch(setTheme(key as THEMES))}
                        >
                            <VisibilityIcon />
                        </Button>
                    </ThemeProvider>
                </Grid>
            ))}
        </Grid>
    );
};

interface ISettingsItemProps {
    label?: string;
}

const SettingsItem: React.FC<ISettingsItemProps> = ({ children, label }) => {
    return (
        <Stack sx={{ padding: 2, paddingBottom: 0 }}>
            {label ? (
                <Typography sx={{ fontSize: 14 }} gutterBottom>
                    {label}
                </Typography>
            ) : null}
            <Box>{children}</Box>
        </Stack>
    );
};

export default NavMenu;
